import {
  autoElement,
  WebModuleTable,
  WebModuleTableColumnDef,
  WebmoduleToggle
} from '../../../webmodule-common/components/src/webmodule-components.js';
import {
  DealerReport,
  ItemSummaryLayout,
  QuoteReportCatalogueEntry,
  QuoteReportOption,
  QuoteReportOptionOverrides,
  QuoteReportOptionValues,
  ReportPriceSummary,
  SupplierReportSetting,
  SupplierSettings
} from '../../api/supplier-api-interface-supplier.js';
import { downloadBlob } from '../../../webmodule-common/other/common/blob-helpers.js';
import { fireQuickSuccessToast } from '../../../webmodule-common/other/toast-away.js';
import { getApiFactory } from '../../api/api-injector.js';
import { getSettingsManager } from '../../supplier/common/supplier-settings.js';
import { getToolTip } from '../../../webmodule-common/other/tooltip.js';
import { html, TemplateResult } from 'lit';
import { isAutoSaving } from '../../../webmodule-common/other/save-workflow.js';
import { LangSettingsReportsView } from '../../../localization/scriptsSupplier/settings/view/settings-reports-view-lang.js';
import { PageControlTabWithIndependantSaving } from '../../../webmodule-common/other/ui/data-entry-screen-base.js';
import { PromiseTemplate, Snippet } from '../../../webmodule-common/interop/webmodule-interop.js';
import { SupplierSettingsManager } from '../../supplier/common/supplier-settings-manager.js';
import type { WebmoduleSelect2Value } from '../../../webmodule-common/components/src/components/select2/select2.js';
import type WebmoduleSelect2 from '../../../webmodule-common/components/src/components/select2/select2.js';

type OptionKey = keyof QuoteReportOptionValues;

// The settings key of each option, so a locked option (named by QuoteReportOption) can be matched to
// the value it locks.
const optionKeys: Record<QuoteReportOption, OptionKey> = {
  [QuoteReportOption.CoverPage]: 'coverPage',
  [QuoteReportOption.InfoPage]: 'infoPage',
  [QuoteReportOption.GlobalSpecifications]: 'globalSpecifications',
  [QuoteReportOption.ItemSummary]: 'itemSummary',
  [QuoteReportOption.QuoteCalculations]: 'quoteCalculations',
  [QuoteReportOption.ItemDetails]: 'itemDetails',
  [QuoteReportOption.Annotations]: 'annotations',
  [QuoteReportOption.DealerTerms]: 'dealerTerms',
  [QuoteReportOption.SupplierTerms]: 'supplierTerms',
  [QuoteReportOption.LineItemPrice]: 'lineItemPrice',
  [QuoteReportOption.GroupTotals]: 'groupTotals',
  [QuoteReportOption.PriceSummary]: 'priceSummary',
  [QuoteReportOption.Dimensions]: 'dimensions',
  [QuoteReportOption.ManufacturerNotes]: 'manufacturerNotes',
  [QuoteReportOption.InitialsPanel]: 'initialsPanel',
  [QuoteReportOption.ItemSummaryLayout]: 'itemSummaryLayout'
};

const sectionOptions: [OptionKey, () => string][] = [
  ['coverPage', LangSettingsReportsView.coverPage],
  ['infoPage', LangSettingsReportsView.infoPage],
  ['globalSpecifications', LangSettingsReportsView.globalSpecifications],
  ['itemSummary', LangSettingsReportsView.itemSummary],
  ['quoteCalculations', LangSettingsReportsView.quoteCalculations],
  ['itemDetails', LangSettingsReportsView.itemDetails],
  ['annotations', LangSettingsReportsView.annotations],
  ['dealerTerms', LangSettingsReportsView.dealerTerms],
  ['supplierTerms', LangSettingsReportsView.supplierTerms]
];

const pricingOptions: [OptionKey, () => string][] = [
  ['lineItemPrice', LangSettingsReportsView.lineItemPrice],
  ['groupTotals', LangSettingsReportsView.groupTotals],
  ['dimensions', LangSettingsReportsView.dimensions]
];

const footerOptions: [OptionKey, () => string][] = [
  ['initialsPanel', LangSettingsReportsView.initialsPanel],
  ['manufacturerNotes', LangSettingsReportsView.manufacturerNotes]
];

export function reportName(report: DealerReport): string {
  switch (report) {
    case DealerReport.ReportA:
      return LangSettingsReportsView.reportA();
    case DealerReport.ReportB:
      return LangSettingsReportsView.reportB();
    case DealerReport.ReportC:
      return LangSettingsReportsView.reportC();
    case DealerReport.ReportG:
      return LangSettingsReportsView.reportG();
    case DealerReport.ReportJ:
      return LangSettingsReportsView.reportJ();
    case DealerReport.ReportE:
      return LangSettingsReportsView.reportE();
    case DealerReport.ReportH:
      return LangSettingsReportsView.reportH();
    default:
      return DealerReport[report];
  }
}

/**
 * The supplier's control over its quote reports: which its dealers can produce, which are kept when a
 * quote is issued, and the options each prints with. Only what differs from the catalogue default is
 * stored (SupplierSettings.quoteReports); the server applies the same rules again on save and when a
 * report is built. See src/documentation/supplier-report-settings-design.md.
 */
@autoElement()
export class SettingsReportsView extends PageControlTabWithIndependantSaving {
  private settingsManager: SupplierSettingsManager = getSettingsManager();

  // The report whose preview is being produced; one at a time.
  private previewing: DealerReport | null = null;

  constructor() {
    super();
    this.pageFragment = 'reports';
  }

  get settings(): SupplierSettings {
    return this.settingsManager.supplierSettings;
  }

  private get catalogue(): QuoteReportCatalogueEntry[] {
    return this.settingsManager.systemSettings?.quoteReports ?? [];
  }

  public allowDeletePage(): boolean {
    return false;
  }

  getValidationErrors(): string[] {
    const errors: string[] = [];
    if (this.catalogue.length > 0 && !this.catalogue.some(x => this.isEnabled(x)))
      errors.push(LangSettingsReportsView.noneAvailable());
    return errors;
  }

  async prepareForSave(): Promise<void> {
    // Settings are edited in place; nothing to copy back.
  }

  public internalDataChanged(): boolean {
    return this.settingsManager.changed();
  }

  async onEnter(): Promise<void> {
    await this.settingsManager.needsSettings(true);
    // Loaded by any settings tab; force it here when an earlier load predates the report catalogue.
    if (!this.settingsManager.systemSettings?.quoteReports) this.settingsManager.systemSettings = undefined;
    await this.settingsManager.needsSystemConfiguration();
    this.requestUpdate();
  }

  protected async revertChanges(): Promise<boolean> {
    this.settingsManager.reset();
    return true;
  }

  protected getCaption(): Snippet {
    return LangSettingsReportsView.caption();
  }

  protected async internalSaveData(): Promise<boolean> {
    const result = await this.settingsManager.saveSettings();
    if (result) {
      if (!isAutoSaving()) fireQuickSuccessToast(LangSettingsReportsView.saved());
      this.requestUpdate();
      return true;
    }
    return false;
  }

  protected async bodyTemplate(): PromiseTemplate {
    return html`<div class="settings-reports">
      <h2 class="settings-reports-heading">
        ${LangSettingsReportsView.heading()} ${getToolTip(LangSettingsReportsView.introduction(), { maxSize: '420px' })}
      </h2>
      <webmodule-table
        class="settings-reports-table"
        .elevated=${false}
        .cellBorders=${false}
        .uppercaseHeader=${false}
        .columns=${this.getColumns()}
        .pageLength=${100}
        .data=${this.catalogue}
        .clickrows=${false}
      ></webmodule-table>
      <webmodule-divider class="settings-reports-divider"></webmodule-divider>
      <h2 class="settings-reports-heading">
        ${LangSettingsReportsView.optionsHeading()}
        ${getToolTip(LangSettingsReportsView.optionsIntroduction(), { maxSize: '420px' })}
      </h2>
      <div class="settings-reports-options">${this.catalogue.map(x => this.optionsTemplate(x))}</div>
    </div>`;
  }

  // --- Reading the effective settings (mirrors QuoteReportCatalogue.Resolve on the server) -------

  private entry(report: DealerReport): SupplierReportSetting | undefined {
    return this.settings.quoteReports?.find(x => x.report === report);
  }

  private isEnabled(definition: QuoteReportCatalogueEntry): boolean {
    return this.entry(definition.report)?.enabled ?? definition.enabledByDefault;
  }

  private isStoredOnIssue(definition: QuoteReportCatalogueEntry): boolean {
    return (
      this.isEnabled(definition) && (this.entry(definition.report)?.storedOnIssue ?? definition.storedOnIssueByDefault)
    );
  }

  private isLocked(definition: QuoteReportCatalogueEntry, key: OptionKey): boolean {
    return definition.locked.some(x => optionKeys[x] === key);
  }

  private optionValue<K extends OptionKey>(definition: QuoteReportCatalogueEntry, key: K): QuoteReportOptionValues[K] {
    const override = this.entry(definition.report)?.options?.[key];
    return this.isLocked(definition, key) || override === null || override === undefined
      ? definition.defaults[key]
      : (override as QuoteReportOptionValues[K]);
  }

  private isOptionChanged(definition: QuoteReportCatalogueEntry, key: OptionKey): boolean {
    return this.optionValue(definition, key) !== definition.defaults[key];
  }

  private isReportChanged(definition: QuoteReportCatalogueEntry): boolean {
    return this.entry(definition.report) !== undefined;
  }

  // --- Writing: only differences from the default are kept, so an untouched report has no entry --

  private updateEntry(report: DealerReport, change: (entry: SupplierReportSetting) => void): void {
    this.settings.quoteReports ??= [];
    let entry = this.entry(report);
    if (!entry) {
      entry = { report, enabled: null, storedOnIssue: null, options: null };
      this.settings.quoteReports.push(entry);
    }
    change(entry);

    if (entry.options && Object.values(entry.options).every(x => x === null || x === undefined)) entry.options = null;
    if (entry.enabled === null && entry.storedOnIssue === null && entry.options === null)
      this.settings.quoteReports.splice(this.settings.quoteReports.indexOf(entry), 1);

    this.requestUpdate();
  }

  private setEnabled(definition: QuoteReportCatalogueEntry, value: boolean): void {
    this.updateEntry(definition.report, entry => {
      entry.enabled = value === definition.enabledByDefault ? null : value;
      // A report that is not available cannot be kept on issue.
      if (!value && entry.storedOnIssue === true) entry.storedOnIssue = null;
    });
  }

  private setStoredOnIssue(definition: QuoteReportCatalogueEntry, value: boolean): void {
    this.updateEntry(definition.report, entry => {
      entry.storedOnIssue = value === definition.storedOnIssueByDefault ? null : value;
    });
  }

  private setOption<K extends OptionKey>(
    definition: QuoteReportCatalogueEntry,
    key: K,
    value: QuoteReportOptionValues[K]
  ): void {
    if (this.isLocked(definition, key)) return;
    this.updateEntry(definition.report, entry => {
      const options: QuoteReportOptionOverrides = entry.options ?? emptyOverrides();
      (options[key] as QuoteReportOptionValues[K] | null) = value === definition.defaults[key] ? null : value;
      entry.options = options;
    });
  }

  // Downloads a preview of the report built from sample data with the settings on screen, saved or not.
  // A failure is shown by the api layer (validation dialog or error message).
  private async preview(definition: QuoteReportCatalogueEntry): Promise<void> {
    if (this.previewing !== null) return;
    this.previewing = definition.report;
    this.requestUpdate();
    try {
      const file = await getApiFactory()
        .supplier()
        .previewQuoteReport({ report: definition.report, quoteReports: this.settings.quoteReports ?? null });
      if (file?.status !== 200) return;

      downloadBlob(file.data, file.fileName ?? `Sample - ${reportName(definition.report)}.pdf`);
    } finally {
      this.previewing = null;
      this.requestUpdate();
    }
  }

  private resetReport(definition: QuoteReportCatalogueEntry): void {
    const entry = this.entry(definition.report);
    if (entry) this.settings.quoteReports!.splice(this.settings.quoteReports!.indexOf(entry), 1);
    this.requestUpdate();
  }

  // --- Templates ---------------------------------------------------------------------------------

  private getColumns(): WebModuleTableColumnDef[] {
    return [
      {
        title: LangSettingsReportsView.columnReport(),
        fieldName: 'report',
        minWidth: '320px',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const definition = item as QuoteReportCatalogueEntry;
          return html`<span class="settings-reports-name">${reportName(definition.report)}</span>
            ${definition.isDealerInternal
              ? html`<webmodule-tag size="small" pill variant="neutral"
                  >${LangSettingsReportsView.internalTag()}</webmodule-tag
                >`
              : ''}
            ${this.isReportChanged(definition)
              ? html`<webmodule-tag size="small" pill variant="primary"
                  >${LangSettingsReportsView.changedTag()}</webmodule-tag
                >`
              : ''}`;
        }
      },
      {
        title: LangSettingsReportsView.columnAvailable(),
        fieldName: 'enabled',
        width: '200px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const definition = item as QuoteReportCatalogueEntry;
          return html`<webmodule-toggle
            size="small"
            ?checked=${this.isEnabled(definition)}
            ?disabled=${this.isReadonly}
            @webmodule-change=${(e: Event) => this.setEnabled(definition, (e.target as WebmoduleToggle).checked)}
            >${hiddenLabel(
              `${LangSettingsReportsView.columnAvailable()}: ${reportName(definition.report)}`
            )}</webmodule-toggle
          >`;
        }
      },
      {
        title: LangSettingsReportsView.columnStoredOnIssue(),
        fieldName: 'storedOnIssue',
        width: '160px',
        align: 'center',
        displayValue: (_table: WebModuleTable, item: unknown) => {
          const definition = item as QuoteReportCatalogueEntry;
          return html`<webmodule-tooltip content=${LangSettingsReportsView.storedOnIssueTooltip()} hoist>
            <span>
              <webmodule-toggle
                size="small"
                ?checked=${this.isStoredOnIssue(definition)}
                ?disabled=${this.isReadonly || !this.isEnabled(definition)}
                @webmodule-change=${(e: Event) =>
                  this.setStoredOnIssue(definition, (e.target as WebmoduleToggle).checked)}
                >${hiddenLabel(
                  `${LangSettingsReportsView.columnStoredOnIssue()}: ${reportName(definition.report)}`
                )}</webmodule-toggle
              >
            </span>
          </webmodule-tooltip>`;
        }
      }
    ];
  }

  private optionsTemplate(definition: QuoteReportCatalogueEntry): TemplateResult {
    return html`<webmodule-details
      class="settings-reports-details"
      summary=${LangSettingsReportsView.optionsSummary({ report: reportName(definition.report) })}
    >
      <div class="settings-reports-option-groups">
        <div class="settings-reports-option-group">
          <small>${LangSettingsReportsView.sectionSections()}</small>
          <webmodule-divider></webmodule-divider>
          ${sectionOptions.map(
            ([key, caption]) =>
              html`${this.switchTemplate(definition, key, caption())}
              ${key === 'itemSummary' ? this.itemSummaryLayoutTemplate(definition) : ''}`
          )}
        </div>
        <div class="settings-reports-option-group">
          <small>${LangSettingsReportsView.sectionPricing()}</small>
          <webmodule-divider></webmodule-divider>
          ${pricingOptions.map(([key, caption]) => this.switchTemplate(definition, key, caption()))}
          ${this.priceSummaryTemplate(definition)}
          <small class="settings-reports-group-gap">${LangSettingsReportsView.sectionFooter()}</small>
          <webmodule-divider></webmodule-divider>
          ${footerOptions.map(([key, caption]) => this.switchTemplate(definition, key, caption()))}
        </div>
      </div>
      <div class="settings-reports-reset">
        <webmodule-button
          size="small"
          variant="default"
          ?disabled=${this.isReadonly || !this.isReportChanged(definition)}
          @click=${() => this.resetReport(definition)}
          >${LangSettingsReportsView.resetToDefaults()}</webmodule-button
        >
        <webmodule-button
          size="small"
          variant="primary"
          ?disabled=${this.isReadonly || this.previewing !== null}
          ?loading=${this.previewing === definition.report}
          @click=${() => this.preview(definition)}
          >${LangSettingsReportsView.preview()}</webmodule-button
        >
      </div>
    </webmodule-details>`;
  }

  private switchTemplate(definition: QuoteReportCatalogueEntry, key: OptionKey, caption: string): TemplateResult {
    const locked = this.isLocked(definition, key);
    const toggle = html`<webmodule-toggle
      size="small"
      ?checked=${this.optionValue(definition, key) as boolean}
      ?disabled=${this.isReadonly || locked}
      @webmodule-change=${(e: Event) => this.setOption(definition, key, (e.target as WebmoduleToggle).checked)}
      >${caption}</webmodule-toggle
    >`;
    return html`<div class="settings-reports-option">
      ${lockedTooltipTemplate(locked, toggle)} ${this.changedTagTemplate(definition, key)}
    </div>`;
  }

  private priceSummaryTemplate(definition: QuoteReportCatalogueEntry): TemplateResult {
    const locked = this.isLocked(definition, 'priceSummary');
    // <webmodule-select> is registered to Select2, which builds its options from .values; slotted
    // <webmodule-option> children are ignored.
    const choices: WebmoduleSelect2Value[] = [
      { value: String(ReportPriceSummary.None), text: LangSettingsReportsView.priceSummaryNone() },
      { value: String(ReportPriceSummary.Summary), text: LangSettingsReportsView.priceSummarySummary() },
      { value: String(ReportPriceSummary.DealerCost), text: LangSettingsReportsView.priceSummaryDealerCost() }
    ];
    const select = html`<webmodule-select
      size="small"
      label=${LangSettingsReportsView.priceSummary()}
      .values=${choices}
      .value=${String(this.optionValue(definition, 'priceSummary'))}
      ?disabled=${this.isReadonly || locked}
      hoist
      @webmodule-change=${(e: Event) =>
        this.setOption(definition, 'priceSummary', Number((e.target as WebmoduleSelect2).value) as ReportPriceSummary)}
    ></webmodule-select>`;
    return html`<div class="settings-reports-option settings-reports-price-summary">
      ${lockedTooltipTemplate(locked, select)} ${this.changedTagTemplate(definition, 'priceSummary')}
    </div>`;
  }

  private itemSummaryLayoutTemplate(definition: QuoteReportCatalogueEntry): TemplateResult {
    const locked = this.isLocked(definition, 'itemSummaryLayout');
    // Only matters while the item summary prints.
    const sectionOff = !this.optionValue(definition, 'itemSummary');
    const choices: WebmoduleSelect2Value[] = [
      { value: String(ItemSummaryLayout.Compact), text: LangSettingsReportsView.itemSummaryLayoutCompact() },
      { value: String(ItemSummaryLayout.Detailed), text: LangSettingsReportsView.itemSummaryLayoutDetailed() }
    ];
    const select = html`<webmodule-select
      size="small"
      label=${LangSettingsReportsView.itemSummaryLayout()}
      .values=${choices}
      .value=${String(this.optionValue(definition, 'itemSummaryLayout'))}
      ?disabled=${this.isReadonly || locked || sectionOff}
      hoist
      @webmodule-change=${(e: Event) =>
        this.setOption(
          definition,
          'itemSummaryLayout',
          Number((e.target as WebmoduleSelect2).value) as ItemSummaryLayout
        )}
    ></webmodule-select>`;
    return html`<div class="settings-reports-option settings-reports-price-summary settings-reports-suboption">
      ${lockedTooltipTemplate(locked, select)} ${this.changedTagTemplate(definition, 'itemSummaryLayout')}
    </div>`;
  }

  private changedTagTemplate(definition: QuoteReportCatalogueEntry, key: OptionKey): TemplateResult | string {
    return this.isOptionChanged(definition, key)
      ? html`<webmodule-tag size="small" pill variant="primary">${LangSettingsReportsView.changedTag()}</webmodule-tag>`
      : '';
  }
}

// A switch's name for assistive technology where the column heading already says it visually. Styled
// inline because the table renders its cells inside its own shadow root, out of reach of page styles.
function hiddenLabel(text: string): TemplateResult {
  return html`<span
    style="position:absolute;width:1px;height:1px;padding:0;margin:-1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap;border:0"
    >${text}</span
  >`;
}

// A locked option's control, wrapped in the tooltip that says why it cannot be changed.
function lockedTooltipTemplate(locked: boolean, control: TemplateResult): TemplateResult {
  return locked
    ? html`<webmodule-tooltip content=${LangSettingsReportsView.lockedTooltip()} hoist
        ><span>${control}</span></webmodule-tooltip
      >`
    : control;
}

function emptyOverrides(): QuoteReportOptionOverrides {
  return {
    coverPage: null,
    infoPage: null,
    globalSpecifications: null,
    itemSummary: null,
    quoteCalculations: null,
    itemDetails: null,
    annotations: null,
    dealerTerms: null,
    supplierTerms: null,
    lineItemPrice: null,
    groupTotals: null,
    priceSummary: null,
    dimensions: null,
    manufacturerNotes: null,
    initialsPanel: null,
    itemSummaryLayout: null
  };
}
